import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Seo from "../../../shared/ui/Seo";
import AccountSidebar from "./AccountSidebar";
import AddAddressModal from "./AddAddressModal";
import { getAddresses, updateAddress, disableAddress, addNewAddress } from "../api";
import "../styles/address-book.css";

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const AddressCard = ({
  address,
  isShipping,
  isBilling,
  onSetDefault,
  onEdit,
  onDelete,
}) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const name = [address.firstName, address.lastName].filter(hasValue).join(" ");
  const area = [address.city, address.state, address.postCode].filter(hasValue).join(", ");
  const phones = [address.phone, address.phone2].filter(hasValue).join(" / ");

  return (
    <article className={`ab-card${isShipping || isBilling ? " is-default" : ""}`}>
      <header className="ab-card__head">
        {name && <h2 className="ab-card__name">{name}</h2>}
        <div className="ab-card__badges">
          {isShipping && <span className="ab-badge"><i className="ph ph-truck" /> Default shipping</span>}
          {isBilling && <span className="ab-badge"><i className="ph ph-receipt" /> Default billing</span>}
        </div>
      </header>

      <div className="ab-card__body">
        {hasValue(address.company) && <p className="ab-card__company">{address.company}</p>}
        {hasValue(address.address1) && <p>{address.address1}</p>}
        {hasValue(address.address2) && <p>{address.address2}</p>}
        {area && <p>{area}</p>}
        {hasValue(address.country) && <p>{address.country}</p>}
        {phones && (
          <p className="ab-card__contact"><i className="ph ph-phone" aria-hidden="true" /> {phones}</p>
        )}
        {hasValue(address.email) && (
          <p className="ab-card__contact"><i className="ph ph-envelope-simple" aria-hidden="true" /> {address.email}</p>
        )}
      </div>

      <footer className="ab-card__foot">
        {confirmingDelete ? (
          <div className="ab-confirm">
            <span>Delete this address?</span>
            <div className="ab-confirm__actions">
              <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </button>
              <button type="button" className="ui-btn ui-btn--danger ui-btn--sm" onClick={() => onDelete(address.id)}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="ab-card__defaults">
              {!isShipping && (
                <button type="button" className="ab-link" onClick={() => onSetDefault(address.id, "shipping")}>
                  Set as default shipping
                </button>
              )}
              {!isBilling && (
                <button type="button" className="ab-link" onClick={() => onSetDefault(address.id, "billing")}>
                  Set as default billing
                </button>
              )}
            </div>
            <div className="ab-card__actions">
              <button type="button" className="ab-icon-btn" onClick={() => onEdit(address)} aria-label="Edit address" title="Edit">
                <i className="ph ph-pencil-simple" />
              </button>
              <button
                type="button"
                className="ab-icon-btn ab-icon-btn--danger"
                onClick={() => setConfirmingDelete(true)}
                aria-label="Delete address"
                title="Delete"
              >
                <i className="ph ph-trash" />
              </button>
            </div>
          </>
        )}
      </footer>
    </article>
  );
};

const AddressBook = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [defaultShippingId, setDefaultShippingId] = useState(null);
  const [defaultBillingId, setDefaultBillingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [editingAddressData, setEditingAddressData] = useState(null);
  const [originalAddressId, setOriginalAddressId] = useState(null);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setHasError(false);
    getAddresses()
      .then((response) => {
        if (ignore || !response) return;
        setAddresses(response.addresses || []);
        if (response.defaultShippingId) setDefaultShippingId(response.defaultShippingId);
        if (response.defaultBillingId) setDefaultBillingId(response.defaultBillingId);
      })
      .catch((error) => {
        console.error("Error fetching addresses:", error.message);
        if (!ignore) setHasError(true);
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  const openNewAddress = () => {
    setOriginalAddressId(null);
    setEditingAddressData(null);
    setIsModalOpen(true);
  };

  const handleEditAddress = (address) => {
    setOriginalAddressId(address.id);
    // Store the full address object for pre-filling the modal
    setEditingAddressData(address);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setOriginalAddressId(null);
    setEditingAddressData(null);
  };

  const handleAddAddress = (newAddress) => {
    setAddresses((prevAddresses) => [...prevAddresses, newAddress]);
    setIsModalOpen(false);
    setEditingAddressData(null);
  };

  const handleSetDefault = async (addressId, addressType) => {
    const setDefault = addressType === "shipping" ? setDefaultShippingId : setDefaultBillingId;
    const previous = addressType === "shipping" ? defaultShippingId : defaultBillingId;
    setDefault(addressId);
    try {
      await updateAddress({ id: addressId, addressType });
      toast.success(`Default ${addressType} address updated.`);
    } catch (error) {
      setDefault(previous);
      toast.error(`Failed to update default ${addressType} address.`);
    }
  };

  const handleDisableAddress = async (addressId) => {
    try {
      await disableAddress(addressId, true);
      setAddresses((prev) => prev.filter((address) => address.id !== addressId));
      toast.success("Address deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete address.");
    }
  };

  // Editing saves a new address and disables the old one (the API has no in-place update).
  const handleEditAddressSubmit = async (updatedData) => {
    if (!originalAddressId) return;
    try {
      const newAddress = await addNewAddress(updatedData);
      await disableAddress(originalAddressId, true);
      setAddresses((prevAddresses) =>
        prevAddresses.filter((addr) => addr.id !== originalAddressId).concat(newAddress)
      );
      setOriginalAddressId(null);
    } catch (error) {
      console.error("Error updating address:", error.message);
      toast.error("Failed to update address. Please try again.");
    }
  };

  // Defaults first, then the rest in API order.
  const isDefault = (address) => address.id === defaultShippingId || address.id === defaultBillingId;
  const sortedAddresses = [...addresses.filter(isDefault), ...addresses.filter((a) => !isDefault(a))];

  let content;
  if (isLoading) {
    content = (
      <div className="ab-grid" aria-hidden="true">
        {[0, 1].map((i) => (
          <div key={i} className="ab-card">
            <span className="skeleton-line w-120" />
            <span className="skeleton-line w-200" />
            <span className="skeleton-line w-200" />
          </div>
        ))}
      </div>
    );
  } else if (hasError) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-warning-circle ui-empty__icon" />
        <h2 className="ui-empty__title">We couldn't load your addresses</h2>
        <p className="ui-empty__text">Please check your connection and try again.</p>
        <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
          Try again
        </button>
      </div>
    );
  } else if (addresses.length === 0) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-map-pin-plus ui-empty__icon" />
        <h2 className="ui-empty__title">No saved addresses</h2>
        <p className="ui-empty__text">Add an address to check out faster next time.</p>
        <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={openNewAddress}>
          <i className="ph ph-plus" />
          Add address
        </button>
      </div>
    );
  } else {
    content = (
      <div className="ab-grid">
        {sortedAddresses.map((address) => (
          <AddressCard
            key={address.id}
            address={address}
            isShipping={address.id === defaultShippingId}
            isBilling={address.id === defaultBillingId}
            onSetDefault={handleSetDefault}
            onEdit={handleEditAddress}
            onDelete={handleDisableAddress}
          />
        ))}
      </div>
    );
  }

  return (
    <section className="account-page py-20">
      <Seo title="Address Book" noindex />
      <div className="account-layout">
        <AccountSidebar />
        <div className="account-main">
          <div className="ab-head">
            <div className="ui-head">
              <h1 className="ui-head__title">Address Book</h1>
              <p className="ui-head__text">Manage your delivery and billing addresses.</p>
            </div>
            {!isLoading && !hasError && addresses.length > 0 && (
              <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={openNewAddress}>
                <i className="ph ph-plus" />
                Add address
              </button>
            )}
          </div>
          {content}
        </div>
      </div>

      <AddAddressModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleAddAddress}
        initialData={editingAddressData}
        onEdit={handleEditAddressSubmit}
      />
    </section>
  );
};

export default AddressBook;
